import React, { useMemo } from "react";
import NotificationPanel from "./NotificationPanel";
import axios from "axios";
import { SERVER_URL } from "../config";
import { useNavigate, useLocation } from "react-router-dom";
import {
  XMarkIcon,
  HomeModernIcon,
  ClockIcon,
  BookmarkIcon,
  ArrowLeftOnRectangleIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  PresentationChartLineIcon,
  DocumentTextIcon,
  BellIcon,
} from "@heroicons/react/24/outline";

const Sidebar = ({ onLogout, isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const employee = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("employee")) || {};
    } catch {
      return {};
    }
  }, []);
  const employeeId = employee?.employee_id;

  const fetchUnread = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${SERVER_URL}/notification_helper.php`, {
        params: { action: "getUnreadCount", employee_id: employeeId },
      });
      if (res.data?.status) setUnreadCount(res.data.count || 0);
    } catch (err) {
      console.error("Unread fetch error:", err);
    }
  };

  React.useEffect(() => {
    if (!employeeId) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [employeeId]);

  const isAdmin = Number(employee?.privilages) === 2;

  const baseNav = [
    { name: "Home", icon: HomeModernIcon, path: "home" },
    { name: "Attendance", icon: ClockIcon, path: "attendance" },
    { name: "CRM", icon: BookmarkIcon, path: "lead" },
    { name: "Notifications", icon: BellIcon, path: "notifications" },
    // { name: "Assignments", icon: ClipboardDocumentCheckIcon, path: "assignments" },
    // { name: "Ticket Generation", icon: ClipboardDocumentCheckIcon, path: "ticketing" },
  ];

  const adminNav = [
    { name: "Attendance Overview", icon: ClipboardDocumentListIcon, path: "attendance-overview" },
    { name: "Leave Overview", icon: DocumentTextIcon, path: "leave-overview" },
    // { name: "Assign Tasks", icon: ClipboardDocumentListIcon, path: "assign-tasks" },
    { name: "Add Product", icon: BuildingOffice2Icon, path: "add-product" },
    // { name: "Statistics", icon: PresentationChartLineIcon, path: "statistics" },
    { name: "Add Employee", icon: UserGroupIcon, path: "add-employee" },
    // { name: "Generate Pay Slip", icon: CurrencyRupeeIcon, path: "generate-payslip" },
    { name: "Expense History", icon: CurrencyRupeeIcon, path: "expense-history" },
  ];

  const NAV_ITEMS = isAdmin ? [...baseNav, ...adminNav] : baseNav;

  const handleNavigation = (path) => {
    if (path === "notifications") {
      setShowNotifications(true);
      if (window.innerWidth < 1024 && onClose) onClose();
      return;
    }
    navigate(`/${path}`);
    if (window.innerWidth < 1024 && onClose) onClose();
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  const avatarLetter = (employee?.employee_name || "U").charAt(0).toUpperCase();

  return (
    <>
      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 4px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .nav-item-active { background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%); box-shadow: 0 4px 14px rgba(99,102,241,0.28); }
        .sidebar-glass { background: rgba(255,255,255,0.97); backdrop-filter: blur(20px); border-right: 1px solid rgba(99,102,241,0.1); }
        .avatar-ring { background: linear-gradient(135deg, #6366f1, #818cf8); }
        .admin-section-label { letter-spacing: 0.1em; }
      `}</style>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 sidebar-glass shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 h-16 px-5 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-200">
              <img
                src="/logo.jpeg"
                alt="Logo"
                className="w-6 h-6 object-contain rounded"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentElement.innerHTML = `<span class="text-white text-xs font-bold">S</span>`;
                }}
              />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800 leading-tight">SAJ Info</div>
              <div className="text-[10px] text-indigo-400 font-semibold tracking-wide uppercase">
                {isAdmin ? "Admin Panel" : "Employee"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Nav */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-0.5">
          {/* Base nav label */}
          <p className="admin-section-label text-[9px] font-bold text-slate-400 uppercase px-3 mb-2">
            Main Menu
          </p>
          {baseNav.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === `/${item.path}` ||
              location.pathname.startsWith(`/${item.path}/`);
            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  isActive
                    ? "nav-item-active text-white"
                    : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                    isActive ? "bg-white/20" : "bg-slate-100 group-hover:bg-indigo-100"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold truncate">{item.name}</span>
                </div>
                {item.path === "notifications" && unreadCount > 0 && (
                  <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[10px] font-bold bg-rose-500 text-white rounded-full shadow-sm">
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}

          {/* Admin section */}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <div className="flex items-center gap-2 px-3">
                  <div className="flex-1 h-px bg-slate-100" />
                  <p className="admin-section-label text-[9px] font-bold text-slate-400 uppercase whitespace-nowrap">
                    Admin Controls
                  </p>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
              </div>
              {adminNav.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === `/${item.path}` ||
                  location.pathname.startsWith(`/${item.path}/`);
                return (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                      isActive
                        ? "nav-item-active text-white"
                        : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                      isActive ? "bg-white/20" : "bg-slate-100 group-hover:bg-indigo-100"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-semibold truncate">{item.name}</span>
                  </button>
                );
              })}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="flex-shrink-0 p-3 border-t border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-9 h-9 rounded-xl avatar-ring flex items-center justify-center text-white text-sm font-bold shadow-md shadow-indigo-200 flex-shrink-0">
              {avatarLetter}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-800 truncate">
                {employee?.employee_name || "Employee"}
              </div>
              <div className="text-xs text-slate-400 truncate">{employee?.job_role || "Staff"}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-rose-500 hover:bg-rose-50 transition-all group"
          >
            <div className="w-8 h-8 rounded-lg bg-rose-50 group-hover:bg-rose-100 flex items-center justify-center transition-all">
              <ArrowLeftOnRectangleIcon className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold">Sign Out</span>
          </button>
        </div>
      </aside>

      {showNotifications && (
        <NotificationPanel
          employeeId={employeeId}
          onClose={() => setShowNotifications(false)}
          refreshCount={() => {}}
        />
      )}
    </>
  );
};

export default Sidebar;